import {
  DeviceData,
  CombinedCheck,
  ActionItem,
  DeviceType
} from '../types';
import {
  parseLiftRange,
  calculateConfidenceRank,
  calculateICEScore
} from '../utils';

/**
 * Service for processing and combining check data
 */
export class CheckService {
  private readonly buckets = [
    'visualAnalysis',
    'copyAnalysis',
    'performanceAnalysis',
    'seoAnalysis',
    'accessibilityAnalysis'
  ] as const;

  private readonly bucketToArea: Record<string, string> = {
    visualAnalysis: 'visual',
    copyAnalysis: 'copy',
    performanceAnalysis: 'performance',
    seoAnalysis: 'seo',
    accessibilityAnalysis: 'accessibility'
  };

  /**
   * Combine checks from mobile and desktop data
   * @param mobileData - Mobile device data
   * @param desktopData - Desktop device data
   * @returns Array of combined checks
   */
  combineChecks(
    mobileData: DeviceData | null,
    desktopData: DeviceData | null
  ): CombinedCheck[] {
    const combinedChecks = new Map<string, CombinedCheck>();

    // Process mobile checks
    if (mobileData) {
      this.processDeviceChecks(mobileData, 'mobile', combinedChecks);
    }

    // Process desktop checks
    if (desktopData) {
      this.processDeviceChecks(desktopData, 'desktop', combinedChecks);
    }

    return Array.from(combinedChecks.values());
  }

  /**
   * Process checks for a specific device
   */
  private processDeviceChecks(
    deviceData: DeviceData,
    device: 'mobile' | 'desktop',
    combinedChecks: Map<string, CombinedCheck>
  ): void {
    this.buckets.forEach(bucket => {
      const analysis = deviceData[bucket];

      if (analysis?.checks) {
        Object.keys(analysis.checks).forEach(checkKey => {
          const check = analysis.checks[checkKey];

          if (!check.pass && check.recommendations?.length) {
            const key = `${bucket}:${checkKey}`;

            if (combinedChecks.has(key)) {
              // Add to existing combined check
              const existing = combinedChecks.get(key)!;
              if (device === 'mobile') {
                existing.mobile = check;
              } else {
                existing.desktop = check;
              }
            } else {
              // Create new combined check
              combinedChecks.set(key, {
                checkKey,
                area: this.bucketToArea[bucket],
                bucket,
                mobile: device === 'mobile' ? check : null,
                desktop: device === 'desktop' ? check : null
              });
            }
          }
        });
      }
    });
  }

  /**
   * Convert combined checks into action items
   * @param combinedChecks - Array of combined checks
   * @returns Array of action items sorted by priority
   */
  processActions(combinedChecks: CombinedCheck[]): ActionItem[] {
    const actions: ActionItem[] = [];

    combinedChecks.forEach(check => {
      // Determine device availability
      const hasMobile = check.mobile !== null;
      const hasDesktop = check.desktop !== null;
      const deviceType: DeviceType = hasMobile && hasDesktop
        ? 'both'
        : hasMobile
        ? 'mobile'
        : 'desktop';

      // Use the first available recommendation for lift calculation
      const sourceCheck = check.mobile || check.desktop;
      if (!sourceCheck || !sourceCheck.recommendations?.length) {
        return;
      }

      const recommendation = sourceCheck.recommendations[0];
      const lift = parseLiftRange(recommendation.solutionDetails?.expectedLiftPct || '').avg;
      const confidence = calculateConfidenceRank(lift);
      const effort = recommendation.solutionDetails?.effort || 3;
      const iceScore = calculateICEScore(confidence, effort, lift);

      actions.push({
        area: check.area,
        checkKey: check.checkKey,
        deviceType,
        mobileCheck: check.mobile,
        desktopCheck: check.desktop,
        recommendation: (check.mobile?.recommendations?.[0] || check.desktop?.recommendations?.[0])!.description,
        problem: sourceCheck.problem || '',
        solutionDetails: recommendation.solutionDetails,
        sortLift: lift,
        confidenceRank: confidence,
        iceScore
      });
    });

    // Sort: combined first, then by confidence/effort/lift
    actions.sort((a, b) => {
      // First, sort by device type: both < mobile < desktop
      const deviceOrder: Record<DeviceType, number> = { both: 0, mobile: 1, desktop: 2 };
      if (deviceOrder[a.deviceType] !== deviceOrder[b.deviceType]) {
        return deviceOrder[a.deviceType] - deviceOrder[b.deviceType];
      }

      // Then by confidence, effort, lift
      if (b.confidenceRank !== a.confidenceRank) {
        return b.confidenceRank - a.confidenceRank;
      }

      const ea = a.solutionDetails.effort || 3;
      const eb = b.solutionDetails.effort || 3;
      if (ea !== eb) {
        return ea - eb;
      }

      return b.sortLift - a.sortLift;
    });

    return actions;
  }

  /**
   * Get top N items by ICE score
   * @param actions - Array of action items
   * @param count - Number of top items to return
   * @returns Set of recommendation descriptions
   */
  getTopICEItems(actions: ActionItem[], count: number = 3): Set<string> {
    const sortedByICE = [...actions].sort((a, b) => b.iceScore - a.iceScore);
    return new Set(sortedByICE.slice(0, count).map(item => item.recommendation));
  }
}
